import type { Doc } from '@doc';
import { layoutNode } from '@doc';
import { LocaleId } from '@strings';
import type { Draft } from 'immer';
import { type CommandContext } from './CommandContext';

// Pending locale selected by UI before command dispatch
export let pendingLocale: LocaleId | undefined;
export function setPendingLocale(locale: LocaleId) {
  pendingLocale = locale;
}

/**
 * Sets the document locale. Layout / drawing side-effects are intentionally deferred.
 * Undo-ready via context.update just like other layout-related commands.
 */
export function setLocale(context: CommandContext): { doc: Doc } | undefined {
  let { doc } = context;
  const locale = pendingLocale;
  if (!locale || doc.locale === locale) return; // No-op

  const oldDoc = doc;
  try {
    const { layouter } = context;
    doc = doc.produce('setLocale', (draft: Draft<Doc>) => {
      draft.locale = locale;
      // Re-layout nodes with new locale (text measurements may change)
      const cache = layouter.cache;
      for (const node of draft.graph.nodes) {
        layoutNode(draft as Doc, node, cache);
      }
    });
    // Reroute edges & adjust positions without animation (incremental layout)
    doc = layouter.runIncrementalLayout(doc, oldDoc.bounds);
    const result = { doc };
    context.update(result, true);
    return result;
  } catch (e) {
    console.error(e);
    return { doc: oldDoc };
  }
}
